import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store';
import { Layout } from '@/components/layout/Layout';
import { useTranslation } from 'react-i18next';
import { PageErrorBoundary } from '@/components/layout/PageErrorBoundary';
import { Button } from '@/components/ui/Button';

const LoginPage = lazy(() => import('@/pages/LoginPage').then((module) => ({ default: module.LoginPage })));
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then((module) => ({ default: module.DashboardPage })));
const AdminPage = lazy(() => import('@/pages/AdminPage').then((module) => ({ default: module.AdminPage })));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  
  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
}

export default function App() {
  const { t } = useTranslation();
  return (
    <PageErrorBoundary fallback={<div role="alert" className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center text-slate-700"><p className="max-w-md">{t('common.pageLoadError')}</p><Button onClick={() => window.location.reload()}>{t('common.retry')}</Button></div>}>
    <Suspense fallback={<div className="flex min-h-screen items-center justify-center text-gray-500">{t('common.loading')}</div>}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="architecture" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="admin/*"
            element={
              <AdminRoute>
                <AdminPage />
              </AdminRoute>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
    </PageErrorBoundary>
  );
}
